import React, { useState, useEffect } from 'react';
import { 
  Search, Upload, Check, Edit, Trash2, X, Filter, 
  Loader2, ChevronLeft, ChevronRight, Download,
  FileText, Table as TableIcon, Linkedin, Twitter, Github,
  Globe, MapPin, Calendar, Briefcase, Award, Phone,
  Mail, UserCircle, Building, GraduationCap, ChevronDown,Clock
} from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const AlumniList = () => {
  // State Management
  const [alumni, setAlumni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [editingAlumni, setEditingAlumni] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [departments, setDepartments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedDegree, setSelectedDegree] = useState('');
  const [allDegrees, setAllDegrees] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState('');
  const [availableBatches, setAvailableBatches] = useState([]);
  const [activeTab, setActiveTab] = useState('personal');
  const [selectedGender, setSelectedGender] = useState('');
  const [showFilters, setShowFilters] = useState(true);

  // Fetch alumni data
  useEffect(() => {
    fetchAlumni();
  }, [departmentFilter, selectedDegree, selectedBatch]);

  useEffect(() => {
    // Extract unique graduation years from alumni data
    const uniqueBatches = [...new Set(alumni.map(a => a.graduationYear))].sort((a, b) => b - a);
    setAvailableBatches(uniqueBatches);
  }, [alumni]);


  const verifyAlumni = async (id) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) throw new Error('No authentication token found');
  
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/college/verify-alumni/${id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
  
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Verification failed');
  
      setUploadResult({
        type: 'success',
        message: 'Alumni verified successfully'
      });
  
      fetchAlumni();
    } catch (err) {
      console.error('Verification error:', err);
      setError(err.message);
      setUploadResult({
        type: 'error',
        message: `Verification failed: ${err.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAlumni = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${process.env.REACT_APP_BASE_URL}/api/college/alumni`;
      const params = [];
      
      if (departmentFilter) params.push(`department=${departmentFilter}`);
      if (selectedDegree) params.push(`degree=${selectedDegree}`);
      if (selectedBatch) params.push(`graduationYear=${selectedBatch}`);
      
      if (params.length > 0) {
        url += `?${params.join('&')}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401) {
        window.location.href = '/login';
        return;
      }

      const data = await response.json();
      const validAlumni = data.filter(alumnus => alumnus.user != null);
      setAlumni(validAlumni);
      setDepartments([...new Set(validAlumni.map(a => a.department))]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this alumni record?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/college/alumni/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Delete failed');
      }

      setUploadResult({
        type: 'success',
        message: 'Alumni deleted successfully'
      });

      fetchAlumni();
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.message);
      setUploadResult({
        type: 'error',
        message: `Delete failed: ${err.message}`
      });
    }
  };

  const handleUpdate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/college/alumni/${editingAlumni._id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firstName: editingAlumni.firstName,
          lastName: editingAlumni.lastName,
          department: editingAlumni.department,
          graduationYear: editingAlumni.graduationYear,
          personalInfo: editingAlumni.personalInfo,
          academicInfo: editingAlumni.academicInfo,
          professionalInfo: editingAlumni.professionalInfo,
          socialLinks: editingAlumni.socialLinks
        })
      });
  
      if (!response.ok) throw new Error('Update failed');
  
      setShowEditModal(false);
      fetchAlumni();
      setUploadResult({
        type: 'success',
        message: 'Alumni information updated successfully'
      });
    } catch (err) {
      setError(err.message);
      setUploadResult({
        type: 'error',
        message: `Update failed: ${err.message}`
      });
    }
  };

  const handleCsvUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/college/upload-csv`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) throw new Error('Upload failed');

      const result = await response.json();
      setUploadResult(result);
      fetchAlumni();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const exportToExcel = () => {
    const exportData = filteredAlumni.map(alumnus => ({
      'Name': `${alumnus.firstName} ${alumnus.lastName}`,
      'Email': alumnus.user?.email || '',
      'Alternate Email': alumnus.personalInfo?.alternateEmail || '',
      'Enrollment Number': alumnus.enrollmentNumber,
      'Degree': alumnus.academicInfo?.degree || '',
      'Department': alumnus.academicInfo?.department || '',
      'Graduation Year': alumnus.graduationYear,
      'Contact Number': alumnus.personalInfo?.contactNumber || '',
      'Gender': alumnus.personalInfo?.gender || '',
      'Status': alumnus.isVerified ? 'Verified' : 'Pending',
      'Skills': alumnus.professionalInfo?.skills?.join(', ') || '',
      'Currently Employed': alumnus.professionalInfo?.currentlyEmployed ? 'Yes' : 'No',
      'LinkedIn': alumnus.socialLinks?.linkedin || '',
      'Github': alumnus.socialLinks?.github || '',
      'Twitter': alumnus.socialLinks?.twitter || '',
      'Portfolio': alumnus.socialLinks?.portfolio || ''
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Alumni');
    XLSX.writeFile(wb, 'alumni_data.xlsx');
  };

  const exportToPdf = () => {
    const doc = new jsPDF('p', 'pt', 'a4');
    
    // Helper function to add a section to the PDF
    const addSection = (title, content, y) => {
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text(title, 40, y);
      doc.setFont(undefined, 'normal');
      doc.setFontSize(10);
      return y + 20;
    };

    filteredAlumni.forEach((alumnus, index) => {
      if (index > 0) {
        doc.addPage();
      }

      let yPos = 40;

      // Header
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text('Alumni Profile', 40, yPos);
      yPos += 30;

      // Personal Information
      yPos = addSection('Personal Information', '', yPos);
      doc.text(`Name: ${alumnus.firstName} ${alumnus.personalInfo?.middleName || ''} ${alumnus.lastName}`, 60, yPos);
      yPos += 20;
      doc.text(`Gender: ${alumnus.personalInfo?.gender || 'Not specified'}`, 60, yPos);
      yPos += 20;
      doc.text(`Contact: ${alumnus.personalInfo?.contactNumber || 'Not specified'}`, 60, yPos);
      yPos += 20;
      doc.text(`Email: ${alumnus.user?.email || 'Not specified'}`, 60, yPos);
      yPos += 20;
      doc.text(`Alt. Email: ${alumnus.personalInfo?.alternateEmail || 'Not specified'}`, 60, yPos);
      yPos += 30;

      // Academic Information
      yPos = addSection('Academic Information', '', yPos);
      doc.text(`Enrollment Number: ${alumnus.enrollmentNumber}`, 60, yPos);
      yPos += 20;
      doc.text(`Degree: ${alumnus.academicInfo?.degree || 'Not specified'}`, 60, yPos);
      yPos += 20;
      doc.text(`Department: ${alumnus.academicInfo?.department || 'Not specified'}`, 60, yPos);
      yPos += 20;
      doc.text(`Graduation Year: ${alumnus.graduationYear}`, 60, yPos);
      yPos += 20;
      
      if (alumnus.academicInfo?.achievements?.length) {
        yPos += 10;
        doc.text('Achievements:', 60, yPos);
        yPos += 20;
        alumnus.academicInfo.achievements.forEach(achievement => {
          doc.text(`• ${achievement}`, 80, yPos);
          yPos += 15;
        });
      }
      yPos += 20;

      // Professional Information
      yPos = addSection('Professional Information', '', yPos);
      doc.text(`Currently Employed: ${alumnus.professionalInfo?.currentlyEmployed ? 'Yes' : 'No'}`, 60, yPos);
      yPos += 20;

      if (alumnus.professionalInfo?.experiences?.length) {
        doc.text('Work Experience:', 60, yPos);
        yPos += 20;
        alumnus.professionalInfo.experiences.forEach(exp => {
          doc.text(`• ${exp.title} at ${exp.company}`, 80, yPos);
          yPos += 15;
          doc.text(`  ${new Date(exp.startDate).toLocaleDateString()} - ${exp.isCurrent ? 'Present' : new Date(exp.endDate).toLocaleDateString()}`, 80, yPos);
          yPos += 15;
          if (exp.description) {
            const lines = doc.splitTextToSize(exp.description, 450);
            lines.forEach(line => {
              doc.text(line, 80, yPos);
              yPos += 15;
            });
          }
          yPos += 10;
        });
      }

      if (alumnus.professionalInfo?.skills?.length) {
        yPos = Math.min(yPos, 700); // Ensure we don't go off the page
        doc.text('Skills:', 60, yPos);
        yPos += 20;
        const skills = alumnus.professionalInfo.skills.join(', ');
        const skillLines = doc.splitTextToSize(skills, 450);
        skillLines.forEach(line => {
          doc.text(line, 80, yPos);
          yPos += 15;
        });
      }

      // Social Links
      if (Object.values(alumnus.socialLinks || {}).some(link => link)) {
        yPos = Math.min(yPos, 750); // Ensure we don't go off the page
        yPos = addSection('Social Links', '', yPos);
        Object.entries(alumnus.socialLinks || {}).forEach(([platform, link]) => {
          if (link) {
            doc.text(`${platform.charAt(0).toUpperCase() + platform.slice(1)}: ${link}`, 60, yPos);
            yPos += 20;
          }
        });
      }
    });

    doc.save('alumni_detailed_report.pdf');
  };


  // Filter calculations
  const filteredAlumni = alumni.filter(alumnus => 
    (searchTerm === '' || 
      alumnus.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      alumnus.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      alumnus.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      alumnus.enrollmentNumber?.toLowerCase().includes(searchTerm.toLowerCase())) &&
    (!selectedDegree || alumnus.academicInfo?.degree === selectedDegree) &&
    (!departmentFilter || alumnus.academicInfo?.department === departmentFilter) &&
    (!selectedBatch || alumnus.graduationYear == selectedBatch) &&
    (!selectedGender || alumnus.personalInfo?.gender === selectedGender)
  );

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentAlumni = filteredAlumni.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredAlumni.length / itemsPerPage);

  // Loading State
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading alumni data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
   

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 space-y-4 md:space-y-0">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Alumni Directory</h1>
            <p className="text-gray-600 mt-1">Manage and track your alumni network</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
  <button
    onClick={exportToExcel}
    className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
  >
    <TableIcon className="w-4 h-4 mr-2" />
    Export Excel
  </button>
  <button
    onClick={exportToPdf}
    className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
  >
    <FileText className="w-4 h-4 mr-2" />
    Export PDF
  </button>
 <a href="https://firebasestorage.googleapis.com/v0/b/xenesis-ff41b.appspot.com/o/tableConvert.com_l9n12j.csv?alt=media&token=ab770028-6845-437d-8000-7b43b4a19d45"> <button
  target='_blank' className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
>
  <Download className="w-4 h-4 mr-2" />
  Download Sample CSV
</button>
</a>
  <label className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors">
    <Upload className="w-4 h-4 mr-2" />
    Upload CSV
    <input
      type="file"
      className="hidden"
      accept=".csv"
      onChange={handleCsvUpload}
    />
  </label>
</div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm mb-6">
          <div className="p-4 border-b border-gray-100">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center justify-between w-full"
            >
              <span className="text-lg font-medium text-gray-900">Filters</span>
              <ChevronDown className={`w-5 h-5 text-gray-500 transform transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>
          
          {showFilters && (
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="text"
                    placeholder="Search alumni..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                {/* Degree Filter */}
                <div className="relative">
                  <GraduationCap className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <select
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                    value={selectedDegree}
                    onChange={(e) => {
                      setSelectedDegree(e.target.value);
                      setDepartmentFilter('');
                    }}
                  >
                    <option value="">All Degrees</option>
                    {allDegrees.map(degree => (
                      <option key={degree.name} value={degree.name}>{degree.name}</option>
                    ))}
                  </select>
                </div>

                {/* Department Filter */}
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <select
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                    value={departmentFilter}
                    onChange={(e) => setDepartmentFilter(e.target.value)}
                    disabled={!selectedDegree}
                  >
                    <option value="">All Departments</option>
                    {selectedDegree && allDegrees
                      .find(d => d.name === selectedDegree)
                      ?.departments.map(dept => (
                        <option key={dept.name} value={dept.name}>{dept.name}</option>
                      ))
                    }
                  </select>
                </div>

                {/* Batch Filter */}
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <select
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                    value={selectedBatch}
                    onChange={(e) => setSelectedBatch(e.target.value)}
                  >
                    <option value="">All Batches</option>
                    {availableBatches.map(batch => (
                      <option key={batch} value={batch}>{batch}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Degree</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Department</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Graduation</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Enrollment</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {currentAlumni.map((alumnus) => (
                  <tr key={alumnus._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          {alumnus.personalInfo?.avatar ? (
                            <img 
                              src={alumnus.personalInfo.avatar} 
                              alt={`${alumnus.firstName}'s avatar`}
                              className="h-10 w-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                              <span className="text-sm font-medium text-white">
                                {alumnus.firstName?.[0]}{alumnus.lastName?.[0]}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {alumnus.firstName} {alumnus.lastName}
                          </div>
                          {alumnus.personalInfo?.contactNumber && (
                            <div className="text-sm text-gray-500 flex items-center">
                              <Phone className="w-3 h-3 mr-1" />
                              {alumnus.personalInfo.contactNumber}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{alumnus.user?.email}</div>
                      {alumnus.personalInfo?.alternateEmail && (
                        <div className="text-xs text-gray-500">Alt: {alumnus.personalInfo.alternateEmail}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{alumnus.academicInfo?.degree || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{alumnus.academicInfo?.department || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {alumnus.graduationYear}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {alumnus.enrollmentNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`
  inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium
  ${alumnus.isVerified 
    ? 'bg-green-100 text-green-800' 
    : 'bg-yellow-100 text-yellow-800'
  }
`}>
                        <div className="flex items-center">
                          {alumnus.user?.isVerified ? (
                            <Check className="w-3 h-3 mr-1" />
                          ) : (
                            <Clock className="w-3 h-3 mr-1" />
                          )}
                          {alumnus.user?.isVerified ? 'Verified' : 'Pending'}
                        </div>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setEditingAlumni(alumnus);
                            setShowEditModal(true);
                          }}
                          className="p-1.5 hover:bg-blue-50 rounded-lg transition-colors text-blue-600"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(alumnus._id)}
                          className="p-1.5 hover:bg-red-50 rounded-lg transition-colors text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        {!alumnus.isVerified && (
  <button
    onClick={() => verifyAlumni(alumnus._id)}
    className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
    disabled={loading}
  >
    {loading ? (
      <Loader2 className="w-3 h-3 animate-spin mr-1" />
    ) : (
      <Check className="w-3 h-3 mr-1" />
    )}
    Verify
  </button>
)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">{indexOfFirstItem + 1}</span>
                  {' '}to{' '}
                  <span className="font-medium">
                    {Math.min(indexOfLastItem, filteredAlumni.length)}
                  </span>
                  {' '}of{' '}
                  <span className="font-medium">{filteredAlumni.length}</span>
                  {' '}results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <span className="sr-only">First</span>
                    <ChevronLeft className="h-5 w-5" />
                    <ChevronLeft className="h-5 w-5 -ml-2" />
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <span className="sr-only">Previous</span>
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  {/* Page numbers */}
                  {[...Array(totalPages)].map((_, idx) => (
                    <button
                      key={idx + 1}
                      onClick={() => setCurrentPage(idx + 1)}
                      className={`
                        relative inline-flex items-center px-4 py-2 border text-sm font-medium
                        ${currentPage === idx + 1
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }
                      `}
                    >
                      {idx + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <span className="sr-only">Next</span>
                    <ChevronRight className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <span className="sr-only">Last</span>
                    <ChevronRight className="h-5 w-5" />
                    <ChevronRight className="h-5 w-5 -ml-2" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-25 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full m-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-6">
              <div className="flex items-center justify-between border-b pb-4">
                <h3 className="text-xl font-semibold text-gray-900">Edit Alumni Profile</h3>
                <button 
                  onClick={() => setShowEditModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Left Column - Personal Information */}
                <div className="space-y-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Personal Information</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          First Name *
                        </label>
                        <input
                          type="text"
                          value={editingAlumni.firstName}
                          onChange={(e) => setEditingAlumni({
                            ...editingAlumni,
                            firstName: e.target.value
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Middle Name
                        </label>
                        <input
                          type="text"
                          value={editingAlumni.personalInfo?.middleName || ''}
                          onChange={(e) => setEditingAlumni({
                            ...editingAlumni,
                            personalInfo: {
                              ...editingAlumni.personalInfo,
                              middleName: e.target.value
                            }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Last Name *
                        </label>
                        <input
                          type="text"
                          value={editingAlumni.lastName}
                          onChange={(e) => setEditingAlumni({
                            ...editingAlumni,
                            lastName: e.target.value
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Gender
                        </label>
                        <select
                          value={editingAlumni.personalInfo?.gender || ''}
                          onChange={(e) => setEditingAlumni({
                            ...editingAlumni,
                            personalInfo: {
                              ...editingAlumni.personalInfo,
                              gender: e.target.value
                            }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Select Gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Date of Birth
                        </label>
                        <input
                          type="date"
                          value={editingAlumni.personalInfo?.dateOfBirth ? new Date(editingAlumni.personalInfo.dateOfBirth).toISOString().split('T')[0] : ''}
                          onChange={(e) => setEditingAlumni({
                            ...editingAlumni,
                            personalInfo: {
                              ...editingAlumni.personalInfo,
                              dateOfBirth: e.target.value
                            }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Contact Number
                        </label>
                        <input
                          type="tel"
                          value={editingAlumni.personalInfo?.contactNumber || ''}
                          onChange={(e) => setEditingAlumni({
                            ...editingAlumni,
                            personalInfo: {
                              ...editingAlumni.personalInfo,
                              contactNumber: e.target.value
                            }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Middle Column - Academic Information */}
                <div className="space-y-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Academic Information</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Enrollment Number *
                        </label>
                        <input
                          type="text"
                          value={editingAlumni.enrollmentNumber}
                          onChange={(e) => setEditingAlumni({
                            ...editingAlumni,
                            enrollmentNumber: e.target.value
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Degree *
                        </label>
                        <select
                          value={editingAlumni.academicInfo?.degree || ''}
                          onChange={(e) => setEditingAlumni({
                            ...editingAlumni,
                            academicInfo: {
                              ...editingAlumni.academicInfo,
                              degree: e.target.value,
                              department: ''
                            }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        >
                          <option value="">Select Degree</option>
                          {allDegrees.map(degree => (
                            <option key={degree.name} value={degree.name}>{degree.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Department *
                        </label>
                        <select
                          value={editingAlumni.academicInfo?.department || ''}
                          onChange={(e) => setEditingAlumni({
                            ...editingAlumni,
                            academicInfo: {
                              ...editingAlumni.academicInfo,
                              department: e.target.value
                            }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                          disabled={!editingAlumni.academicInfo?.degree}
                        >
                          <option value="">Select Department</option>
                          {editingAlumni.academicInfo?.degree && allDegrees
                            .find(d => d.name === editingAlumni.academicInfo.degree)
                            ?.departments.map(dept => (
                              <option key={dept.name} value={dept.name}>{dept.name}</option>
                            ))
                          }
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Graduation Year *
                        </label>
                        <input
                          type="number"
                          value={editingAlumni.graduationYear}
                          onChange={(e) => setEditingAlumni({
                            ...editingAlumni,
                            graduationYear: e.target.value
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Academic Achievements
                        </label>
                        <textarea
                          value={editingAlumni.academicInfo?.achievements?.join('\n') || ''}
                          onChange={(e) => setEditingAlumni({
                            ...editingAlumni,
                            academicInfo: {
                              ...editingAlumni.academicInfo,
                              achievements: e.target.value.split('\n').filter(Boolean)
                            }
                          })}
                          placeholder="Enter achievements (one per line)"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-32"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Professional & Social Information */}
                <div className="space-y-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Professional & Social Information</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Currently Employed
                        </label>
                        <div className="flex items-center gap-4">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              checked={editingAlumni.professionalInfo?.currentlyEmployed === true}
                              onChange={() => setEditingAlumni({
                                ...editingAlumni,
                                professionalInfo: {
                                  ...editingAlumni.professionalInfo,
                                  currentlyEmployed: true
                                }
                              })}
                              className="mr-2"
                            />
                            Yes
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              checked={editingAlumni.professionalInfo?.currentlyEmployed === false}
                              onChange={() => setEditingAlumni({
                                ...editingAlumni,
                                professionalInfo: {
                                  ...editingAlumni.professionalInfo,
                                  currentlyEmployed: false
                                }
                              })}
                              className="mr-2"
                            />
                            No
                          </label>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Skills
                        </label>
                        <textarea
                          value={editingAlumni.professionalInfo?.skills?.join('\n') || ''}
                          onChange={(e) => setEditingAlumni({
                            ...editingAlumni,
                            professionalInfo: {
                              ...editingAlumni.professionalInfo,
                              skills: e.target.value.split('\n').filter(Boolean)
                            }
                          })}
                          placeholder="Enter skills (one per line)"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-32"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Social Links
                        </label>
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <Linkedin className="w-4 h-4 text-gray-400" />
                            <input
                              type="url"
                              placeholder="LinkedIn URL"
                              value={editingAlumni.socialLinks?.linkedin || ''}
                              onChange={(e) => setEditingAlumni({
                                ...editingAlumni,
                                socialLinks: {
                                  ...editingAlumni.socialLinks,
                                  linkedin: e.target.value
                                }
                              })}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <Twitter className="w-4 h-4 text-gray-400" />
                            <input
                              type="url"
                              placeholder="Twitter URL"
                              value={editingAlumni.socialLinks?.twitter || ''}
                              onChange={(e) => setEditingAlumni({
                                ...editingAlumni,
                                socialLinks: {
                                  ...editingAlumni.socialLinks,
                                  twitter: e.target.value
                                }
                              })}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <Github className="w-4 h-4 text-gray-400" />
                            <input
                              type="url"
                              placeholder="GitHub URL"
                              value={editingAlumni.socialLinks?.github || ''}
                              onChange={(e) => setEditingAlumni({
                                ...editingAlumni,
                                socialLinks: {
                                  ...editingAlumni.socialLinks,
                                  github: e.target.value
                                }
                              })}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <Globe className="w-4 h-4 text-gray-400" />
                            <input
                              type="url"
                              placeholder="Portfolio URL"
                              value={editingAlumni.socialLinks?.portfolio || ''}
                              onChange={(e) => setEditingAlumni({
                                ...editingAlumni,
                                socialLinks: {
                                  ...editingAlumni.socialLinks,
                                  portfolio: e.target.value
                                }
                              })}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-6 border-t">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdate}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error/Success Notification */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-lg shadow-lg">
          <div className="flex items-center">
            <X className="w-5 h-5 mr-2" />
            <p>{error}</p>
            <button
              onClick={() => setError('')}
              className="ml-4 text-red-500 hover:text-red-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      
{uploadResult && (
  <div className={`fixed bottom-4 right-4 ${
    uploadResult.message.toLowerCase().includes('success') || uploadResult.message.toLowerCase().includes('completed')
      ? 'bg-green-100 border-green-500 text-green-700' 
      : 'bg-red-100 border-red-500 text-red-700'
  } border-l-4 p-4 rounded-lg shadow-lg`}>
    <div className="flex items-center">
      {uploadResult.message.toLowerCase().includes('success') || uploadResult.message.toLowerCase().includes('completed') ? (
        <Check className="w-5 h-5 mr-2" />
      ) : (
        <X className="w-5 h-5 mr-2" />
      )}
      <p>{uploadResult.message}</p>
      <button
        onClick={() => setUploadResult(null)}
        className={`ml-4 ${
          uploadResult.message.toLowerCase().includes('success') || uploadResult.message.toLowerCase().includes('completed')
            ? 'text-green-500 hover:text-green-600' 
            : 'text-red-500 hover:text-red-600'
        }`}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  </div>
)}
    </div>
  );
};

export default AlumniList;